import { Link, useLocation } from 'react-router';

/**
 * The admin area's navigation, which until now did not exist.
 *
 * Everything the coach could reach hung off a client: the roster was the home
 * page and the wordmark was the only way back to it. Templates are the first
 * object that is hers rather than one client's, so they cannot hang off a client
 * workspace and something had to appear.
 *
 * **Two header links, not a navigation system** (design D7). A left sidebar with
 * sections was considered and rejected: at two items it is two hundred pixels of
 * chrome carrying one word, and it pre-commits a layout that the exercise
 * library and instance settings would make us redraw. Two links extend to four
 * without redrawing anything, which is what the queue behind this — the library,
 * then messages — actually needs.
 *
 * The active section is carried by `aria-current="page"` and shown by weight and
 * an underline, never by colour alone.
 */

export type AdminSection = 'clients' | 'templates';

/**
 * Which section a path belongs to.
 *
 * `Clients` is the fallback rather than an equal branch, because the roster is
 * the admin area's home: `/admin` itself, every client workspace under it, and
 * anything added beside them later all read as "clients" until they are given a
 * section of their own. Pure, so the rule is asserted directly rather than
 * through a rendered header.
 */
export function adminSection(pathname: string): AdminSection {
  return pathname === '/admin/templates' || pathname.startsWith('/admin/templates/')
    ? 'templates'
    : 'clients';
}

const LINKS: { section: AdminSection; to: string; label: string }[] = [
  { section: 'clients', to: '/admin', label: 'Clients' },
  { section: 'templates', to: '/admin/templates', label: 'Templates' },
];

export function AdminNav() {
  const section = adminSection(useLocation().pathname);

  return (
    <nav aria-label="Admin sections" className="flex items-center gap-1">
      {LINKS.map((link) => {
        const active = link.section === section;
        return (
          <Link
            key={link.to}
            to={link.to}
            aria-current={active ? 'page' : undefined}
            className={[
              'rounded-[--radius-control] px-3 py-2 text-sm underline-offset-8',
              // Weight and an underline, so the current section is legible to
              // someone who cannot tell these two colours apart.
              active
                ? 'text-ink font-semibold underline decoration-2'
                : 'text-ink-muted hover:text-ink font-medium',
            ].join(' ')}
          >
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
}
